import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Skeleton, Stats } from '@/components/ui/daisy';
import { Colors } from '@/components/ui/types';
import useKlassesPendingSummaryTotal from '@/data/hook/useKlassesPendingSummaryTotal';
import { KlassesPendingSummaryTotalFilters } from '@/data/services/klassAnalyticsServices';
import { PendenciesTabEnum } from '@/pages/pendencies/PendenciesPage';
import { buildPendenciesTabLink } from '@/utils/klasses/buildPendenciesTabLink';

type statsMap = {
  title: string;
  value: number | undefined;
  color: Extract<Colors, 'info' | 'secondary' | 'accent'>;
  link: string;
};

type PendenciesCountProps = {
  filters?: KlassesPendingSummaryTotalFilters;
  className?: string;
};

export function PendenciesCount({ filters, className }: PendenciesCountProps) {
  const { t } = useTranslation('translation', { keyPrefix: 'pendencies' });
  const { pendingCount, isLoading } = useKlassesPendingSummaryTotal(filters);

  const klassId = filters?.id?.length === 1 ? filters.id[0] : undefined;

  const stats: statsMap[] = [
    {
      title: t('tabs.attendance'),
      value: pendingCount?.pendingReportsCount,
      color: 'info',
      link: buildPendenciesTabLink(PendenciesTabEnum.ATTENDANCE, klassId),
    },
    {
      title: t('tabs.exams'),
      value: pendingCount?.pendingExamsCount,
      color: 'secondary',
      link: buildPendenciesTabLink(PendenciesTabEnum.EXAMS, klassId),
    },
    {
      title: t('tabs.activities'),
      value: pendingCount?.pendingActivitiesCount,
      color: 'accent',
      link: buildPendenciesTabLink(PendenciesTabEnum.ACTIVITIES, klassId),
    },
  ];

  return (
    <div
      className={twMerge(
        'rounded-2xl border border-base-content/10 flex justify-center items-center h-36',
        className,
      )}
    >
      <ConditionalRenderer
        condition={!isLoading}
        fallback={
          <Skeleton
            className={twMerge('h-36 rounded-2xl min-w-full', className)}
          />
        }
      >
        <Stats className="size-full auto-cols-fr">
          {stats.map(stat => (
            <Stats.Stat key={stat.link} className="px-1 sm:px-6">
              <Link
                to={stat.link}
                className="flex flex-col justify-center items-center"
              >
                <Stats.Title className="font-bold text-base-content/60">
                  {stat.title}
                </Stats.Title>
                <Stats.Value color={stat.color}>{stat.value}</Stats.Value>
                <Stats.Description>{t('outstanding')}</Stats.Description>
              </Link>
            </Stats.Stat>
          ))}
        </Stats>
      </ConditionalRenderer>
    </div>
  );
}
